import styled from '@emotion/styled';
import { Theme, useMediaQuery } from '@mui/material';
import { Modal } from 'components/molecules/Modal/Modal';
import { CenterModalContentStyle, CenterModalStyle } from 'components/molecules/Modal/style';
import QUERY_KEYS from 'network/config/queryKeys';
import { getMadinaPaymentConfig, getMadinaPaymentMethods } from 'network/services/madina';
import { formatCurrency } from 'utils/currencyFormater';
import { getStoredBranchId } from 'utils/branch';
import { buildTier1PaymentOptions, type Tier1PaymentSelection } from 'utils/madinaPayment';
import { useQuery } from 'react-query';
import { PaymentModalCon } from './style';
import { COLORS } from 'theme/color';

type Tier1PaymentModalProps = {
  amount: number;
  onSelect: (selection: Tier1PaymentSelection) => void;
  onClose: () => void;
  isLoading?: boolean;
};

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OptionButton = styled.button<{ disabled?: boolean }>`
  width: 100%;
  text-align: left;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px 16px;
  background: ${({ disabled }) => (disabled ? '#f9fafb' : '#fff')};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: ${({ disabled }) => (disabled ? '#e5e7eb' : COLORS.MainBlue)};
    box-shadow: ${({ disabled }) => (disabled ? 'none' : '0 4px 12px rgba(0, 0, 0, 0.06)')};
  }

  .top-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 6px;
  }

  .title {
    font-size: 16px;
    font-weight: 700;
    color: #111827;
  }

  .badge {
    font-size: 11px;
    font-weight: 600;
    color: ${COLORS.MainBlue};
    background: #eef4ff;
    border-radius: 999px;
    padding: 4px 8px;
    white-space: nowrap;
  }

  .description {
    font-size: 13px;
    line-height: 1.45;
    color: #6b7280;
  }
`;

const GatewayNote = styled.p`
  font-size: 12px;
  line-height: 1.5;
  color: #6b7280;
  margin: 0 0 12px;
  padding: 10px 12px;
  background: #f9fafb;
  border-radius: 8px;
`;

export const Tier1PaymentModal = ({ amount, onSelect, onClose, isLoading }: Tier1PaymentModalProps) => {
  const sm = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));
  const branchId = getStoredBranchId();

  const { data: methods = [], isLoading: loadingMethods } = useQuery({
    queryKey: [QUERY_KEYS.MADINA_PAYMENT_METHODS, branchId],
    queryFn: () => getMadinaPaymentMethods(branchId!),
    enabled: !!branchId
  });

  const { data: paymentConfig, isLoading: loadingConfig } = useQuery({
    queryKey: [QUERY_KEYS.MADINA_PAYMENT_CONFIG, branchId],
    queryFn: getMadinaPaymentConfig,
    enabled: !!branchId
  });

  const paymentOptions = buildTier1PaymentOptions(methods, paymentConfig);
  const isLoadingOptions = loadingMethods || loadingConfig;
  const onionPayAvailable = paymentOptions.some(option => option.badge === 'OnionPay');

  const gatewayNote = onionPayAvailable
    ? 'Card opens OnionPay. The order is paid only after Madina confirms it.'
    : 'Choose a payment method from the list below.';

  return (
    <Modal
      onModalClose={onClose}
      extraModalContentStyle={{ ...CenterModalContentStyle, width: sm ? '520px' : '' }}
      extraModalStyle={CenterModalStyle}>
      <PaymentModalCon>
        <h3>Payment Method</h3>
        <div className="total-amount">
          <span>Total Amount</span>
          <span className="amount">{formatCurrency(amount)}</span>
        </div>

        <GatewayNote>{gatewayNote}</GatewayNote>

        <OptionList>
          {isLoadingOptions && <p>Loading payment methods...</p>}
          {!isLoadingOptions && paymentOptions.length === 0 && (
            <p>No payment methods are available for this branch right now.</p>
          )}
          {paymentOptions.map(option => (
            <OptionButton
              key={option.id}
              type="button"
              disabled={isLoading}
              onClick={() => onSelect(option.selection)}>
              <div className="top-row">
                <span className="title">{option.title}</span>
                {option.badge ? <span className="badge">{option.badge}</span> : null}
              </div>
              <p className="description">{option.description}</p>
            </OptionButton>
          ))}
        </OptionList>
      </PaymentModalCon>
    </Modal>
  );
};

export default Tier1PaymentModal;
